import React, { useEffect, useState, useRef } from 'react';
import { Smartphone, Monitor, Eye, Loader2 } from 'lucide-react';
import api from '../../api/client';

/**
 * Live preview pane for an email template.
 *
 * For SYSTEM templates, calls `/api/email-templates/{type}/preview` with a
 * `body_html` override so the user sees the FULL rendered email (header,
 * shell, signature) — not just the body fragment they edit.
 *
 * For USER templates, renders the body_html as-is in a sandboxed iframe.
 *
 * Debounced 350 ms so we don't hammer the backend on every keystroke.
 */
export default function EmailLivePreview({ template, subject, bodyHtml, title }) {
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'mobile'
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!template) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        if (template.kind === 'user') {
          // Plain HTML wrapped in a tiny shell so the user sees it like a real email
          const safeBody = bodyHtml || '<p style="color:#B5B6C9;font-style:italic">Aperçu vide</p>';
          setPreviewHtml(`
            <!DOCTYPE html>
            <html>
            <head><meta charset="utf-8"><style>
              body { margin:0; padding:24px; font-family: 'Inter', system-ui, sans-serif; color: #1A1B2E; background: #F6F7FB; }
              .wrap { max-width: 560px; margin: 0 auto; background: #fff; border-radius: 12px; padding: 28px; box-shadow: 0 1px 3px rgba(0,0,0,.05); }
              h1, h2 { color: #1A1B2E; }
              h2 { font-size: 18px; font-weight: 700; margin: 8px 0 6px; }
              h3 { font-size: 15px; font-weight: 700; margin: 6px 0 4px; }
              p { margin: 0 0 12px; line-height: 1.55; font-size: 14px; }
              a { color: #4F46E5; }
              ul, ol { margin: 0 0 12px; padding-left: 22px; }
              .subj { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #8E8FA5; margin-bottom: 8px; }
            </style></head>
            <body>
              <div class="wrap">
                <div class="subj">Objet · ${escapeHtml(subject || '—')}</div>
                ${safeBody}
              </div>
            </body>
            </html>
          `);
        } else {
          // System template — call backend preview endpoint with override body
          const { data } = await api.post(`/email-templates/${template.type}/preview`, {
            body_html: bodyHtml,
            subject,
            title,
          });
          setPreviewHtml(data.html);
        }
      } catch {
        setPreviewHtml('<p style="padding:24px;color:#B91C1C">Aperçu indisponible.</p>');
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [template, subject, bodyHtml, title]);

  if (!template) return null;
  const width = device === 'mobile' ? 380 : '100%';

  return (
    <div className="soft-card p-0 overflow-hidden h-full flex flex-col" data-testid="email-live-preview">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#EFF0F6] bg-[#FAFAFE] shrink-0">
        <div className="flex items-center gap-2 text-[12.5px] font-semibold text-[#1A1B2E]">
          <Eye className="w-3.5 h-3.5 text-[#4F46E5]" />
          Aperçu en direct
          {loading && <Loader2 className="w-3 h-3 animate-spin text-[#8E8FA5]" />}
        </div>
        <div className="flex items-center gap-0.5 bg-white border border-[#EFF0F6] rounded-lg p-0.5">
          <button
            type="button"
            data-testid="preview-device-desktop"
            onClick={() => setDevice('desktop')}
            title="Aperçu desktop"
            className={`h-7 px-2 rounded-md text-[11px] font-semibold flex items-center gap-1 transition-colors ${device === 'desktop' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#F6F7FB]'}`}
          >
            <Monitor className="w-3 h-3" /> Desktop
          </button>
          <button
            type="button"
            data-testid="preview-device-mobile"
            onClick={() => setDevice('mobile')}
            title="Aperçu mobile"
            className={`h-7 px-2 rounded-md text-[11px] font-semibold flex items-center gap-1 transition-colors ${device === 'mobile' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#F6F7FB]'}`}
          >
            <Smartphone className="w-3 h-3" /> Mobile
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-[#E8E9F1] flex items-start justify-center p-4">
        <iframe
          data-testid="email-preview-iframe"
          title="email-preview"
          srcDoc={previewHtml}
          sandbox="allow-same-origin"
          className="bg-white border-0 rounded-lg shadow-md transition-all duration-200"
          style={{
            width,
            minHeight: 480,
            height: '100%',
            maxWidth: '100%',
          }}
        />
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]);
}
